/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';

import {ThreadStatus, ThreadType} from '../util/interfaces';

import {IndexedObject, ModelResponse, ResponseData} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import Account from './accountModel';

import {finishThread, getThreads, stopThread} from '../util/requestSender';

import {IconName} from '@fortawesome/fontawesome-svg-core';

interface Props extends BaseProps {
    account_id?: string;
    status_changed_at?: string;
    status?: ThreadStatus;
    type?: ThreadType;
    flex?: IndexedObject<any>;
    process_id?: string;
    account?: Account | null | any;
}

interface Fields extends BaseFields {
    account_id: string;
    status_changed_at: string;
    status: ThreadStatus;
    type: ThreadType;
    flex: IndexedObject<any>;
    process_id: string;
    account: Account | null;
}

const fields: Fields = {
    id: '',
    created_at: '',
    updated_at: '',
    account_id: '',
    status_changed_at: '',
    status: ThreadStatus.RUNNING,
    type: ThreadType.PERIODIC,
    process_id: '',
    account: null,
    flex: {}
};

const fieldsDefinition: {[key in keyof Omit<Fields, 'account'>]: FieldDefinition} = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    account_id: createFieldDef(ModelFieldType.STRING),
    status_changed_at: createFieldDef(ModelFieldType.STRING),
    status: createFieldDef(ModelFieldType.STRING, ThreadStatus.RUNNING),
    type: createFieldDef(ModelFieldType.STRING, ThreadType.PERIODIC),
    flex: createFieldDef(ModelFieldType.OBJECT),
    process_id: createFieldDef(ModelFieldType.STRING)
};

const ignoreProps: Array<keyof Props> = ['account'];

export default class ThreadMonitor extends BaseModel<Props, Fields> {
    static getAll = (limit: number, page: number, callback: ModelResponse<ThreadMonitor[]>) => {
        getThreads({
            limit,
            page,
            callback: (data: ResponseData) => {
                const threads: ThreadMonitor[] = data.success
                    ? data.threads.map((thread: any) => new ThreadMonitor(thread))
                    : [];
                callback(threads, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get accountId(): string {
        return this.fields.account_id;
    }

    set accountId(account_id: string) {
        this.fields.account_id = account_id;
    }

    get statusChangedAt(): string {
        return this.fields.status_changed_at;
    }

    set statusChangedAt(status_changed_at: string) {
        this.fields.status_changed_at = status_changed_at;
    }

    get statusChangedAtMoment(): IDate {
        return new IDate(this.statusChangedAt);
    }

    get status(): ThreadStatus {
        return this.fields.status;
    }

    get translatedStatus(): string {
        switch (this.status) {
            case ThreadStatus.RUNNING:
                return 'Actief';
            case ThreadStatus.SHOULD_STOP:
                return 'Wordt gestopt';
            case ThreadStatus.STOPPED:
                return 'Gestopt';
            case ThreadStatus.FINISHED:
                return 'Afgerond';
            case ThreadStatus.WAITING:
                return 'Wachten';
        }
    }

    set status(status: ThreadStatus) {
        this.fields.status = status;
    }

    get type(): ThreadType {
        return this.fields.type;
    }

    get translatedType(): string {
        switch (this.type) {
            case ThreadType.HISTORY:
                return 'Historie importeren';
            case ThreadType.PERIODIC:
                return 'Periodiek importeren';
            case ThreadType.HISTORY_SENDER:
                return 'Historie zender';
            case ThreadType.MAILCHIMP_SYNC:
                return 'Mailchimp sync';
            case ThreadType.MAILERLITE_SYNC:
                return 'MailerLite sync';
            case ThreadType.PENDING_SENDER:
                return 'Te verzenden facturen check';
            case ThreadType.PRODUCT_IMPORTER:
                return 'Producten importeren';
            case ThreadType.INVOICE_EXPORT:
                return 'Facturen exporteren';
            case ThreadType.RETURNS_IMPORT:
                return 'Retouren importeren';
            case ThreadType.VAT_DECLARATION:
                return 'BTW-aangifte';
            case ThreadType.EXPORT_VAT_DECLARATION:
                return 'BTW-aangifte export';
            case ThreadType.INVOICE_REQUEST_IMPORTER:
                return 'Factuuraanvragen importeren';
            case ThreadType.BOL_PRODUCT_SYNC:
                return 'Bol product sync';
            case ThreadType.RATINGS_IMPORT:
                return 'Reviews importeren';
            case ThreadType.RATINGS_EXPORT:
                return 'Reviews exporteren';
        }
    }

    set type(type: ThreadType) {
        this.fields.type = type;
    }

    get account(): Account | null {
        return this.fields.account;
    }

    set account(account: Account | null) {
        this.fields.account = account;
        if (account) this.fields.account_id = account.id;
    }

    get icon(): IconName {
        switch (this.type) {
            case ThreadType.HISTORY:
                return 'reply';
            case ThreadType.HISTORY_SENDER:
                return 'envelope';
            case ThreadType.INVOICE_EXPORT:
            case ThreadType.EXPORT_VAT_DECLARATION:
                return 'file-export';
            case ThreadType.MAILCHIMP_SYNC:
            case ThreadType.MAILERLITE_SYNC:
                return 'mailchimp';
            case ThreadType.PENDING_SENDER:
                return 'paper-plane';
            case ThreadType.PERIODIC:
            case ThreadType.INVOICE_REQUEST_IMPORTER:
                return 'file-invoice';
            case ThreadType.PRODUCT_IMPORTER:
            case ThreadType.BOL_PRODUCT_SYNC:
                return 'box';
            case ThreadType.RETURNS_IMPORT:
                return 'truck-loading';
            case ThreadType.VAT_DECLARATION:
                return 'euro-sign';
            case ThreadType.RATINGS_IMPORT:
            case ThreadType.RATINGS_EXPORT:
                return 'star';
        }
    }

    get processID() {
        return this.fields.process_id;
    }

    getFlex = (key: string): any | null => {
        return this.fields.flex[key] ?? null;
    };

    stop = (callback: ModelResponse<ThreadMonitor>) => {
        stopThread({
            id: this.id,
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.thread);
                callback(this, data.success, data);
            }
        });
    };

    finish = (callback: ModelResponse<ThreadMonitor>) => {
        finishThread({
            id: this.id,
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.thread);
                callback(this, data.success, data);
            }
        });
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            if (props.account) {
                this.account = props.account instanceof Account ? props.account : new Account(props.account);
            } else this.account = null;
        }
    };
}
